import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { FormControl } from '@angular/forms';

function generateCurrentHour(timezone: string = 'UTC'): {hour: string, minute: string} {
  const date = new Date();
  if (timezone === 'BRT') {
    date.setTime(date.getTime() - 1000 * 60 * 60 * 3);
  }
  const dateString = date.toISOString();
  return {
    hour: dateString.substr(11, 2),
    minute: (Math.floor(parseInt(dateString.substr(14, 2), 10) / 10) * 10).toString().padStart(2, '0')
  };
}

function explodeDateParts(value: string) {
  const [time, timezone] = value.split(' ');
  if (isValidTimezoneString(time)) {
    const {hour: hourPart, minute: minutePart} = generateCurrentHour(time);
    return {hour: parseInt(hourPart, 10), minute: parseInt(minutePart, 10), timezone: time};
  }
  const [hourStr, minuteStr] = time.split(':');
  const [hour, minute] = [parseInt(hourStr, 10), parseInt(minuteStr, 10)];
  return {hour, minute, timezone};
}

function implodeDateParts(hour: number, minute: number, timeZone: string): string {
  return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')} ${timeZone}`;
}

function isValidHourString(value: string): boolean {
  return value !== '' && !value.match(/[^\d]/g) && parseInt(value, 10) >= 0 && parseInt(value, 10) < 24;
}

function isValidMinuteString(value: string): boolean {
  return value !== '' && !value.match(/[^\d]/g) && parseInt(value, 10) >= 0 && parseInt(value, 10) < 60;
}

function isValidTimezoneString(value: string): boolean {
  return ['BRT', 'UTC'].includes(value);
}

@Component({
  selector: 'app-hour-minute-input',
  templateUrl: './hour-minute-input.component.html',
  styleUrls: ['./hour-minute-input.component.scss']
})
export class HourMinuteInputComponent implements OnInit {

  @Input() control: FormControl;
  @Input() disableMinute: boolean;
  @Output() inputChange = new EventEmitter<void>();

  public value: string;
  public valid: boolean;
  public isNegativeHour = false;

  public hour = {
    value: '',
    get valid() { return isValidHourString(this.value); }
  };

  public minute = {
    value: '',
    get valid() { return isValidMinuteString(this.value); }
  };

  public timezone = {
    value: 'UTC',
    get valid() { return isValidTimezoneString(this.value); }
  };

  constructor() { }

  ngOnInit() {
    if (!this.control || !this.control.value) {
      // Generate a UTC date based on current time
      const {hour, minute} = generateCurrentHour('UTC');
      this.hour.value = hour;
      this.minute.value = minute;
      this.timezone.value = 'UTC';
    } else {
      // Use given input value
      const { hour, minute, timezone } = explodeDateParts(this.control.value.toString());
      this.hour.value = hour.toString().padStart(2, '0');
      this.minute.value = minute.toString().padStart(2, '0');
      this.timezone.value = timezone;
    }
    this.updateInputData();
  }

  updateInputData() {
    const isValid = this.hour.valid && this.minute.valid;
    if (isValid !== this.valid) {
      this.valid = isValid;
      if (this.control) {
        this.control.setErrors(isValid ? null : { required: true });
      }
      this.inputChange.emit();
    }
    if (!isValid) {
      return;
    }
    const value = implodeDateParts(parseInt(this.hour.value, 10), parseInt(this.minute.value, 10), this.timezone.value);
    if (value !== this.value) {
      this.value = value;
      if (this.control) {
        this.control.setValue(value);
        this.control.markAsDirty();
        this.control.markAsTouched();
        this.inputChange.emit();
      }
    }
  }

  onInputChange(change: {value: string, valid: boolean}, event) {
    change.value = event.target.value;
    this.updateInputData();
  }

  get localTime(): string {
    const hourUTC = parseInt(this.hour.value, 10);
    const minuteUTC = parseInt(this.minute.value, 10);
    if (isNaN(hourUTC) || isNaN(minuteUTC)) {
      return '...';
    }
    const c = new Date();
    const minutesOffset = ((new Date()).getTimezoneOffset());
    let hourLocal: string | number = hourUTC - Math.round(minutesOffset / 60);
    if (hourLocal < 0) {
      hourLocal = '* ' + (hourLocal + 24);
      this.isNegativeHour = true;
    } else {
      this.isNegativeHour = false;
    }
    const minuteLocal = minuteUTC - Math.round(minutesOffset % 60);
    const sign = (minutesOffset > 0 ? '-' : '+');
    const tzHour = Math.round(minutesOffset / 60).toString().padStart(2, '0');
    const tzMinute = Math.round(minutesOffset % 60).toString().padStart(2, '0');
    const timezoneOffsetString = sign + tzHour + ':' + tzMinute;
    return `${hourLocal.toString().padStart(2, '0')}:${minuteLocal.toString().padStart(2, '0')} [${timezoneOffsetString}]`;
  }

}
